import { useState } from 'react';
import { callUiApi } from '../../../common/fetch';
import { t } from '../../../common/i18n';
import { ContactRole } from '../../../generated/api/contactRole';
import { ContactType } from '../../../generated/api/contactType';
import { DuplicateContactResult } from '../../../generated/api/duplicateContactResult';
import { createContactPrivateMethod } from '../../../generated/api/uiApiMethods';
import type { Contact } from '../../../generated/api/contact';
import type { ContactHeader } from '../../../generated/api/contactHeader';
import type { GenericErrorDuplicateContact } from '../../../generated/api/genericErrorDuplicateContact';
import type { OrderItemPurposeOrContact } from '../placeOrderTypes';

export interface CreateContactResult {
  success: boolean;
  contactId?: string;
  duplicateContact?: ContactHeader;
  duplicateType?: DuplicateContactResult.TypeEnum;
  error?: string;
}

interface DuplicateDialogState {
  isOpen: boolean;
  duplicateContact: GenericErrorDuplicateContact | null;
  duplicateType: DuplicateContactResult.TypeEnum | undefined;
  pendingData: OrderItemPurposeOrContact | null;
}

interface UseDuplicateContactHandlerReturn {
  dialogState: DuplicateDialogState;
  createContact: (data: OrderItemPurposeOrContact) => Promise<CreateContactResult>;
  handleSelectDuplicate: () => string | undefined;
  handleForceCreate: () => Promise<string | undefined>;
  handleCancel: () => void;
  canForceCreate: boolean;
}

const createContactData = (data: OrderItemPurposeOrContact): Contact | null => {
  const { email, firstName, lastName, phoneNumber } = data;

  if (!email || !firstName || !lastName || !phoneNumber) {
    return null;
  }

  return {
    contactType: ContactType.PERSON,
    person: {
      email,
      firstName,
      lastName,
      phoneNumber,
      roles: [ContactRole.BILLING_CONTACT],
    },
  };
};

const postContactWithForceUpsert = async (contactData: Contact, forceUpsert = false): Promise<CreateContactResult> => {
  try {
    const response = await callUiApi(createContactPrivateMethod({ forceUpsert }), {
      body: JSON.stringify(contactData),
      noThrow: true, // Don't throw on 409, we want to handle it
    });

    if (response.ok) {
      const responseData = await response.json();
      return { success: true, contactId: responseData.contactId };
    }

    // Handle 409 Conflict - duplicate contact
    if (response.status === 409) {
      const errorData: DuplicateContactResult = await response.json();

      return {
        success: false,
        duplicateContact: errorData.duplicateContact,
        duplicateType: errorData.type,
      };
    }

    return { success: false, error: `Failed to create contact: ${response.status}` };
  } catch (error) {
    return { success: false, error: String(error) };
  }
};

const toGenericErrorDuplicateContact = (contact?: ContactHeader): GenericErrorDuplicateContact | null => {
  if (!contact?.contactId) {
    return null;
  }
  return {
    contactId: contact.contactId,
    email: contact.email ?? '',
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    phoneNumber: contact.phoneNumber ?? '',
  };
};

/**
 * Hook to handle duplicate contact detection and resolution during contact creation.
 *
 * Usage:
 * 1. Call `createContact(data)` when user submits new contact
 * 2. If duplicate detected, dialog state becomes open
 * 3. Render DuplicateContactDialog using `dialogState`
 * 4. Handle user choice via `handleSelectDuplicate`, `handleForceCreate`, or `handleCancel`
 */
export const useDuplicateContactHandler = (): UseDuplicateContactHandlerReturn => {
  const [dialogState, setDialogState] = useState<DuplicateDialogState>({
    isOpen: false,
    duplicateContact: null,
    duplicateType: undefined,
    pendingData: null,
  });

  const closeDialog = () => {
    setDialogState({
      isOpen: false,
      duplicateContact: null,
      duplicateType: undefined,
      pendingData: null,
    });
  };

  const createContact = async (data: OrderItemPurposeOrContact): Promise<CreateContactResult> => {
    const contactData = createContactData(data);

    if (!contactData) {
      return { success: false, error: t.EQK0('Some contact information is missing') };
    }

    const result = await postContactWithForceUpsert(contactData, false);

    if (result.success) {
      return result;
    }

    // Handle duplicate types
    if (result.duplicateType && result.duplicateContact) {
      // DUPLICATE_FROM_HIERARCHY: Auto force-create (can't use contact from hierarchy)
      if (result.duplicateType === DuplicateContactResult.TypeEnum.DUPLICATE_FROM_HIERARCHY) {
        return postContactWithForceUpsert(contactData, true);
      }

      // CONTACT_SUPPORT or CONFLICTING_CONTACT: Can't resolve, return error
      if (
        result.duplicateType === DuplicateContactResult.TypeEnum.CONTACT_SUPPORT ||
        result.duplicateType === DuplicateContactResult.TypeEnum.CONFLICTING_CONTACT
      ) {
        return { success: false, error: result.duplicateType };
      }

      // DUPLICATE_CONTACT or POSSIBLE_CONTACT_DUPLICATE: Show dialog
      const genericDuplicateContact = toGenericErrorDuplicateContact(result.duplicateContact);
      if (genericDuplicateContact) {
        setDialogState({
          isOpen: true,
          duplicateContact: genericDuplicateContact,
          duplicateType: result.duplicateType,
          pendingData: data,
        });
      }

      return result;
    }

    return result;
  };

  /**
   * User chose to use the found duplicate contact.
   * Returns the duplicate contact's ID.
   */
  const handleSelectDuplicate = (): string | undefined => {
    const contactId = dialogState.duplicateContact?.contactId;
    closeDialog();
    return contactId;
  };

  /**
   * User chose to force create a new contact anyway.
   * Only available for POSSIBLE_CONTACT_DUPLICATE type.
   * Returns the newly created contact's ID.
   */
  const handleForceCreate = async (): Promise<string | undefined> => {
    if (!dialogState.pendingData) {
      closeDialog();
      return undefined;
    }

    const contactData = createContactData(dialogState.pendingData);
    if (!contactData) {
      closeDialog();
      return undefined;
    }

    const result = await postContactWithForceUpsert(contactData, true);
    closeDialog();

    if (result.success) {
      return result.contactId;
    }

    return undefined;
  };

  /**
   * User cancelled - close dialog without action.
   */
  const handleCancel = () => {
    closeDialog();
  };

  // Force create is only allowed for possible duplicates (not exact duplicates)
  const canForceCreate = dialogState.duplicateType === DuplicateContactResult.TypeEnum.POSSIBLE_CONTACT_DUPLICATE;

  return {
    dialogState,
    createContact,
    handleSelectDuplicate,
    handleForceCreate,
    handleCancel,
    canForceCreate,
  };
};
